/**
 * Auth Hooks
 * 
 * React hooks for authentication using TanStack Query
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import { useAuthStore, type User as StoreUser } from '../stores/auth';
import { useFieldsStore } from '../stores/fields';
import {
    login as loginApi,
    logout as logoutApi,
    getCurrentUser,
    updateCurrentUser,
    requestPasswordReset,
    resetPassword,
    getPolicyGlobals,
    type LoginCredentials,
    type User as ApiUser,
} from '../lib/auth';

/**
 * Convert API User type to Store User type
 */
function toStoreUser(apiUser: ApiUser): StoreUser {
    return {
        id: apiUser.id,
        email: apiUser.email,
        first_name: apiUser.first_name ?? undefined,
        last_name: apiUser.last_name ?? undefined,
        role: apiUser.role ?? undefined,
        avatar: apiUser.avatar ?? undefined,
    };
}

/**
 * Hook to handle login
 */
export function useLogin() {
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const { setUser, setTokens, setGlobals } = useAuthStore();
    const { hydrate: hydrateFields } = useFieldsStore();

    return useMutation({
        mutationFn: async (credentials: LoginCredentials) => {
            const response = await loginApi(credentials);

            // Store tokens
            setTokens(response.access_token, response.refresh_token);

            // Fetch user data and policy globals in parallel (mirrors reference)
            const [user, globals] = await Promise.all([
                getCurrentUser(),
                getPolicyGlobals(),
            ]);

            setUser(toStoreUser(user));
            setGlobals(globals);

            // Hydrate global stores after successful login
            await hydrateFields();

            return { ...response, user };
        },
        onSuccess: () => {
            // Invalidate all queries to refetch with auth
            queryClient.invalidateQueries();
            navigate('/admin');
        },
        onError: (error) => {
            console.error('Login failed:', error);
        },
    });
}

/**
 * Hook to handle logout
 */
export function useLogout() {
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const { refreshToken, logout: clearAuth } = useAuthStore();
    const { dehydrate: dehydrateFields } = useFieldsStore();

    return useMutation({
        mutationFn: async () => {
            await logoutApi(refreshToken || undefined);
        },
        onSettled: () => {
            // Always clear local state, even if API call fails
            clearAuth();
            dehydrateFields();
            queryClient.clear();
            navigate('/auth/login');
        },
    });
}

/**
 * Hook to get current user
 */
export function useCurrentUser() {
    const { isAuthenticated, accessToken, setUser } = useAuthStore();

    return useQuery({
        queryKey: ['currentUser'],
        queryFn: async () => {
            const user = await getCurrentUser();
            setUser(toStoreUser(user));
            return user;
        },
        enabled: isAuthenticated && !!accessToken,
        staleTime: 5 * 60 * 1000, // 5 minutes
        retry: false,
    });
}

/**
 * Hook to update current user
 */
export function useUpdateCurrentUser() {
    const queryClient = useQueryClient();
    const { setUser } = useAuthStore();

    return useMutation({
        mutationFn: (data: Partial<ApiUser>) => updateCurrentUser(data),
        onSuccess: (user) => {
            setUser(toStoreUser(user));
            queryClient.setQueryData(['currentUser'], user);
        },
    });
}

/**
 * Hook to request password reset
 */
export function useRequestPasswordReset() {
    return useMutation({
        mutationFn: (email: string) => requestPasswordReset(email),
    });
}

/**
 * Hook to reset password
 */
export function useResetPassword() {
    const navigate = useNavigate();

    return useMutation({
        mutationFn: ({ token, password }: { token: string; password: string }) =>
            resetPassword(token, password),
        onSuccess: () => {
            navigate('/auth/login');
        },
    });
}

/**
 * Hook to check if user is authenticated
 */
export function useAuth(): {
    isAuthenticated: boolean;
    user: StoreUser | null;
    accessToken: string | null;
    refreshToken: string | null;
} {
    const { isAuthenticated, user, accessToken, refreshToken } = useAuthStore();

    return {
        isAuthenticated,
        user,
        accessToken,
        refreshToken,
    };
}

/**
 * Initialize auth on app load
 * This should be called once at app startup
 */
export function useInitializeAuth() {
    const { accessToken, refreshToken, setUser, setGlobals, logout } = useAuthStore();
    const { hydrate: hydrateFields, hydrated: fieldsHydrated } = useFieldsStore();

    return useQuery({
        queryKey: ['initAuth'],
        queryFn: async () => {
            // If no tokens, nothing to initialize
            if (!accessToken || !refreshToken) {
                return null;
            }

            try {
                // Fetch user and policy globals in parallel (mirrors reference)
                const [user, globals] = await Promise.all([
                    getCurrentUser(),
                    getPolicyGlobals(),
                ]);

                setUser(toStoreUser(user));
                setGlobals(globals);

                // Hydrate global stores if not already done
                if (!fieldsHydrated) {
                    await hydrateFields();
                }

                return user;
            } catch (error) {
                // Only logout if token is definitely invalid (401/403)
                // Other errors (network, server down) should not clear auth
                if (error instanceof Error && 'status' in error) {
                    const status = (error as { status: number }).status;
                    if (status === 401 || status === 403) {
                        logout();
                    }
                }
                return null;
            }
        },
        // Only run if we have tokens but aren't yet authenticated
        enabled: !!accessToken && !!refreshToken,
        staleTime: Infinity, // Only run once per session
        retry: false,
    });
}
